import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { getProject } from '@/utils/api';
import { useSettings } from '@/context/SettingsContext';
import { motion, AnimatePresence } from 'framer-motion';

// ==================== COMPONENTE LIGHTBOX/CARRUSEL ====================
const ImageCarousel = ({ images, initialIndex = 0, onClose, projectName }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[110] w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
      >
        <X size={24} />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous button */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* Main image */}
      <div className="max-w-[90vw] max-h-[85vh] relative z-[105]" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={images[currentIndex]?.url}
            alt={images[currentIndex]?.alt || `${projectName} - ${currentIndex + 1}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        </AnimatePresence>
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto py-2 px-4">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img
                src={img.url}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ==================== COMPONENTE GALERÍA CARRUSEL ====================
const GalleryCarousel = ({ images, projectName, primaryColor, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  const goNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1));
  };

  const goPrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // Auto-scroll carousel
  useEffect(() => {
    if (carouselRef.current) {
      const scrollAmount = currentIndex * (carouselRef.current.offsetWidth * 0.75);
      carouselRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [currentIndex]);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative">
      {/* Main carousel */}
      <div
        ref={carouselRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {images.map((img, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-3/4 md:w-1/2 lg:w-2/5 snap-start cursor-pointer group relative"
            onClick={() => onImageClick(idx)}
          >
            <img
              src={img.url}
              alt={img.alt || `${projectName} - ${idx + 1}`}
              className="w-full aspect-[4/3] object-cover rounded-2xl transition-transform duration-300 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-2xl flex items-center justify-center">
              <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
            </div>
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      {images.length > 2 && (
        <>
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all ${
              currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
            style={{ color: primaryColor }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goNext}
            disabled={currentIndex >= images.length - 1}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all ${
              currentIndex >= images.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
            style={{ color: primaryColor }}
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'w-6' : 'bg-slate-300 hover:bg-slate-400'
              }`}
              style={idx === currentIndex ? { backgroundColor: primaryColor } : {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================
export const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNave, setSelectedNave] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  const { settings } = useSettings();
  const heroVideoRef = useRef(null);

  const primaryColor = settings?.theme?.primaryColor || '#1e3a5f';

  useEffect(() => {
    loadProject();
  }, [slug]);

  useEffect(() => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = true;
      heroVideoRef.current.play().catch(err => console.log('Video autoplay failed:', err));
    }
  }, [project?.hero?.videoUrl]);

  const loadProject = async () => {
    try {
      const data = await getProject(slug);
      setProject(data);
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-slate-600">Cargando proyecto...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Proyecto no encontrado</h1>
          <Link to="/proyectos" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} /> Volver a proyectos
          </Link>
        </div>
      </div>
    );
  }

  const hero = project.hero || {};
  const stats = project.stats || [];
  const naves = project.naves || [];
  const areasComunes = project.areasComunes || [];
  const ubicacionInfo = project.ubicacionInfo || {};
  const images = project.media?.images || [];

  // Helper para obtener el color del estado
  const getEstadoColor = (estado) => {
    const lower = estado?.toLowerCase() || '';
    if (lower.includes('disponible')) return 'bg-emerald-500';
    if (lower.includes('ocupada')) return 'bg-red-500';
    if (lower.includes('renovación') || lower.includes('renovacion')) return 'bg-amber-500';
    return 'bg-slate-500';
  };

  // Helper para URL de mapa embed
  const getMapEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('/maps/embed')) return url;
    return url;
  };

  const mapEmbedUrl = getMapEmbedUrl(project.mapEmbedUrl);
  const heroVideoUrl = hero.videoUrl || project.media?.videos?.[0]?.url || '';

  return (
    <main className="bg-white min-h-screen">
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <ImageCarousel
            images={lightboxImages}
            initialIndex={lightboxIndex}
            onClose={closeLightbox}
            projectName={project.nombre}
          />
        )}
      </AnimatePresence>

      {/* ==================== HERO ==================== */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {heroVideoUrl && (
          <video
            ref={heroVideoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={heroVideoUrl} type="video/mp4" />
          </video>
        )}
        {!heroVideoUrl && images[0] && (
          <img 
            src={images[0].url} 
            alt={project.nombre}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/50" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div 
              className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase mb-6"
              style={{ backgroundColor: `${primaryColor}30`, color: primaryColor, border: `1px solid ${primaryColor}50` }}
            >
              {hero.tag || 'Proyecto Destacado'}
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              {hero.titulo || project.nombre}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              {hero.subtitulo || project.descripcionCorta}
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-sm flex flex-col items-center gap-2">
          <span>Explorá</span>
          <div className="w-px h-8 bg-white/30" />
        </div>
      </section>

      {/* ==================== DESCRIPCIÓN ==================== */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
            Descripción General
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            {project.nombre}
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed max-w-3xl" style={{ textAlign: 'justify' }}>
            {project.descripcionLarga}
          </p>

          {stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              {stats.map((stat, idx) => (
                <div 
                  key={idx} 
                  className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200"
                >
                  <div className="text-3xl md:text-4xl font-bold" style={{ color: primaryColor }}>
                    {stat.value}
                  </div>
                  <div className="text-slate-600 text-sm mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          )}

          {project.features?.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Características</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {project.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-slate-600">
                    <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==================== GALERÍA CARRUSEL ==================== */}
      {images.length > 0 && (
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
              Galería
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">Imágenes del Proyecto</h2>
            
            <GalleryCarousel
              images={images}
              projectName={project.nombre}
              primaryColor={primaryColor}
              onImageClick={(idx) => openLightbox(images, idx)}
            />
          </div>
        </section>
      )}

      {/* ==================== NAVES ==================== */}
      {naves.length > 0 && (
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
              Infraestructura
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Nuestras Naves</h2>
            <p className="text-slate-600 max-w-2xl mb-12">
              Cada nave está diseñada para adaptarse a distintas operaciones logísticas e industriales.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {naves.map((nave, idx) => (
                <div 
                  key={nave.id || idx}
                  className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => setSelectedNave(nave)}
                  data-testid={`nave-card-${idx}`}
                >
                  <div className="relative aspect-video">
                    {nave.imagen ? (
                      <img 
                        src={nave.imagen} 
                        alt={nave.nombre}
                        width="800"
                        height="500"
                        loading="eager"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400">
                        Sin imagen
                      </div>
                    )}
                    <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium text-white ${getEstadoColor(nave.estadoCustom || nave.estado)}`}>
                      {nave.estadoCustom || nave.estado}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{nave.nombre}</h3>
                    <div className="flex gap-4 text-sm text-slate-500 mb-3">
                      {nave.superficie && <span>📐 {nave.superficie}</span>}
                      {nave.altura && <span>📏 {nave.altura}</span>}
                    </div>
                    <p className="text-slate-500 text-sm line-clamp-2">{nave.descripcion}</p>
                  </div>
                  <div 
                    className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm font-medium group-hover:bg-slate-100 transition-colors"
                    style={{ color: primaryColor }}
                  >
                    Ver detalles <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== ÁREAS COMUNES ==================== */}
      {(areasComunes.length > 0 || project.areasComunesVideo) && (
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
              Espacios Compartidos
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Áreas Comunes</h2>
            <p className="text-slate-600 max-w-2xl mb-12">
              El parque cuenta con infraestructura compartida de primer nivel.
            </p>

            {areasComunes.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {areasComunes.map((area, idx) => (
                  <div 
                    key={idx} 
                    className="relative group overflow-hidden rounded-xl cursor-pointer"
                    onClick={() => openLightbox(areasComunes.map(a => ({ url: a.imagen, alt: a.label })), idx)}
                  >
                    <img 
                      src={area.imagen} 
                      alt={area.label}
                      width="600"
                      height="400"
                      loading="eager"
                      className="w-full aspect-[3/2] object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-3 left-3 text-white text-sm font-medium">
                      {area.label}
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {project.areasComunesVideo && (
              <div className="mt-8">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full rounded-2xl"
                >
                  <source src={project.areasComunesVideo} type="video/mp4" />
                </video>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ==================== UBICACIÓN ==================== */}
      {(mapEmbedUrl || ubicacionInfo.direccion) && (
        <section className="py-20 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
              Ubicación
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Cómo Llegar</h2>
            <p className="text-slate-600 max-w-2xl mb-8">
              {project.ubicacion}
            </p>

            {mapEmbedUrl && (
              <div className="rounded-2xl overflow-hidden mb-8 border border-slate-200">
                <iframe
                  src={mapEmbedUrl}
                  className="w-full h-96"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación del proyecto"
                />
              </div>
            )}

            {(ubicacionInfo.direccion || ubicacionInfo.accesos || ubicacionInfo.transporte) && (
              <div className="grid md:grid-cols-3 gap-6">
                {ubicacionInfo.direccion && (
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">Dirección</h4>
                    <p className="text-slate-600 text-sm whitespace-pre-line">{ubicacionInfo.direccion}</p>
                  </div>
                )}
                {ubicacionInfo.accesos && (
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">Accesos</h4>
                    <p className="text-slate-600 text-sm whitespace-pre-line">{ubicacionInfo.accesos}</p>
                  </div>
                )}
                {ubicacionInfo.transporte && (
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">Transporte</h4>
                    <p className="text-slate-600 text-sm whitespace-pre-line">{ubicacionInfo.transporte}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ==================== CTA FINAL ==================== */}
      <section className="py-16 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            ¿Te interesa este proyecto?
          </h2>
          <p className="text-slate-600 mb-8">
            Contactanos para más información sobre disponibilidad y condiciones.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
              data-testid="project-contact-btn"
            >
              Consultar sobre este proyecto
            </Link>
            <Link
              to="/proyectos"
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-sm font-semibold text-slate-700 border border-slate-300 hover:bg-white transition-colors"
            >
              <ArrowLeft size={16} /> Ver otros proyectos
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== MODAL DETALLE NAVE ==================== */}
      <AnimatePresence>
        {selectedNave && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedNave(null)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                {selectedNave.imagen ? (
                  <img 
                    src={selectedNave.imagen} 
                    alt={selectedNave.nombre}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 bg-slate-100" />
                )}
                <button 
                  onClick={() => setSelectedNave(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 text-slate-700 flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: primaryColor }}>
                  Nave
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{selectedNave.nombre}</h2>
                <p className="text-slate-600 mb-6">{selectedNave.descripcion}</p>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {selectedNave.superficie && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="text-slate-500 text-xs mb-1">Superficie</div>
                      <div className="text-slate-900 font-semibold">{selectedNave.superficie}</div>
                    </div>
                  )}
                  {selectedNave.altura && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="text-slate-500 text-xs mb-1">Altura</div>
                      <div className="text-slate-900 font-semibold">{selectedNave.altura}</div>
                    </div>
                  )}
                  {selectedNave.techo && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="text-slate-500 text-xs mb-1">Tipo de Techo</div>
                      <div className="text-slate-900 font-semibold">{selectedNave.techo}</div>
                    </div>
                  )}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="text-slate-500 text-xs mb-1">Estado</div>
                    <div className="text-slate-900 font-semibold">{selectedNave.estadoCustom || selectedNave.estado}</div>
                  </div>
                  {selectedNave.instalaciones && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="text-slate-500 text-xs mb-1">Instalaciones</div>
                      <div className="text-slate-900 font-semibold">{selectedNave.instalaciones}</div>
                    </div>
                  )}
                  {selectedNave.accesos && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="text-slate-500 text-xs mb-1">Detalles</div>
                      <div className="text-slate-900 font-semibold">{selectedNave.accesos}</div>
                    </div>
                  )}
                </div>

                {/* Galería de la nave con carrusel */}
                {selectedNave.galeria?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Galería</h3>
                    <GalleryCarousel
                      images={selectedNave.galeria}
                      projectName={selectedNave.nombre}
                      primaryColor={primaryColor}
                      onImageClick={(idx) => openLightbox(selectedNave.galeria, idx)}
                    />
                  </div>
                )}

                {/* Video de la nave */}
                {selectedNave.video && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Video</h3>
                    <video
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded-xl"
                    >
                      <source src={selectedNave.video} type="video/mp4" />
                    </video>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};
